import { useEffect, useRef } from 'react';
import L from 'leaflet';
import { MAP_CONFIG } from './mapConfig';

interface Props {
  map: L.Map;
}

const BOUNDS: L.LatLngBoundsExpression = [
  [0, 0],
  [MAP_CONFIG.height, MAP_CONFIG.width],
];

// Bump MAP_IMAGE_VERSION whenever assets-source/map.png changes (after
// running `node scripts/optimize-map-image.mjs`). The query string busts
// browser HTTP caches and the SW runtime cache for users still pinned
// to the previous map by their cached entries — without it, the
// unhashed asset URLs collide with the long-lived CDN/HTTP cache and
// the user has to hard-refresh to see a new map.
//
// Match the version used in index.html's <link rel="preload"> so the
// preload and the in-React fetch hit the same cache key.
const MAP_IMAGE_VERSION = '4';
const LQIP_URL = `/assets/images/map-lqip.png?v=${MAP_IMAGE_VERSION}`;
const AVIF_URL = `/assets/images/map.avif?v=${MAP_IMAGE_VERSION}`;
const WEBP_URL = `/assets/images/map.webp?v=${MAP_IMAGE_VERSION}`;
const PNG_URL = `/assets/images/map.png?v=${MAP_IMAGE_VERSION}`;

// Full-image format candidates, best first. We swap straight to AVIF (which
// index.html preloads at high priority) and fall back through the list via the
// overlay's own `error` event. The previous approach probed each URL with a
// separate `new Image()` before calling setUrl — that extra async hop +
// throwaway request made Lantern model the LCP as a late, throttled download,
// inflating the simulated mobile LCP to ~5s even though the image actually
// painted in ~250ms.
const FULL_IMAGE_CANDIDATES = [AVIF_URL, WEBP_URL, PNG_URL];

/**
 * Apply crisp-edges rendering to the underlying <img> element of an
 * imageOverlay. This keeps the LQIP from looking like a bilinear smear
 * while it is being upscaled to the full map bounds.
 */
function applyCrispRendering(overlay: L.ImageOverlay) {
  const el = overlay.getElement() as HTMLImageElement | undefined;
  if (!el) return;
  // `crisp-edges` is the broadly supported keyword; some browsers map it
  // to nearest-neighbor (which is what we want for the upscaled LQIP).
  el.style.imageRendering = 'crisp-edges';
}

/**
 * Promote the image overlay to its own GPU compositor layer so its
 * raster texture is kept at source resolution and each zoom scale is
 * applied via the compositor instead of re-rasterizing the image.
 *
 * Without this, Chrome re-paints the 2000×3000 source at every new
 * compositor scale during a zoom for subpixel sharpness — which is
 * the dominant cause of "zoom feels laggy, gets better after a few
 * zooms" on this map (the browser caches rasterizations per scale).
 *
 * Leaflet's built-in CSS promotes SVG layers the same way
 * (`svg.leaflet-zoom-animated { will-change: transform }`) but not
 * image overlays, so we add the hint ourselves. Trade-off: at extreme
 * upscales the image is texture-scaled on the GPU and will appear
 * slightly softer than a fresh raster would — acceptable for our
 * max zoom range and much better than the janky animation.
 */
function promoteToCompositorLayer(overlay: L.ImageOverlay) {
  const el = overlay.getElement() as HTMLImageElement | undefined;
  if (!el) return;
  el.style.willChange = 'transform';
}

/**
 * Remove the crisp-edges hint once we have the full-resolution image —
 * we want smooth interpolation for the actual map artwork.
 */
function clearCrispRendering(overlay: L.ImageOverlay) {
  const el = overlay.getElement() as HTMLImageElement | undefined;
  if (!el) return;
  el.style.imageRendering = '';
}

export function MapImageOverlay({ map }: Props) {
  const overlayRef = useRef<L.ImageOverlay | null>(null);
  const cancelledRef = useRef(false);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    if (!map) return;

    cancelledRef.current = false;

    // Register zoom listeners BEFORE adding the overlay so they fire
    // before Leaflet's own zoom→_reset handler (Leaflet registers its
    // layer handlers in addTo, so anything registered earlier runs first).
    //
    // Why we need this: promoteToCompositorLayer sets will-change:transform
    // permanently so the compositor keeps a cached texture between zooms.
    // But when Leaflet's _reset() fires it does two things in sequence:
    //   1. setPosition(img) — updates the CSS transform (removes scale)
    //   2. img.style.width / height — layout change
    // With will-change:transform the compositor thread applies the transform
    // change independently of the main-thread layout, producing a brief
    // frame where the image has the new (no-scale) transform but the OLD
    // pixel dimensions — the "grows from smaller to full size" flash.
    // Clearing will-change before _reset() forces both changes through the
    // same main-thread paint cycle so the compositor sees them atomically.
    function onZoom() {
      const el = overlayRef.current?.getElement() as HTMLImageElement | undefined;
      if (el) el.style.willChange = '';
    }
    function onZoomEnd() {
      // Re-promote after _reset() has committed the new dimensions so the
      // compositor layer is ready at the correct size for the next zoom.
      // rAF ensures the new width/height have been painted before we hand
      // the element back to the compositor.
      //
      // Cancel any pending rAF from a previous zoomend (rapid scroll-wheel
      // can queue multiple discrete zoom steps before any frame fires).
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = null;
        if (cancelledRef.current) return;
        const el = overlayRef.current?.getElement() as HTMLImageElement | undefined;
        if (el) el.style.willChange = 'transform';
      });
    }
    map.on('zoom', onZoom);
    map.on('zoomend', onZoomEnd);

    // 1. Mount the LQIP immediately so the user sees something on first
    //    paint. CRITICAL: assign the ref BEFORE registering any async
    //    handlers that might reference it.
    const overlay = L.imageOverlay(LQIP_URL, BOUNDS, {
      opacity: 1,
      interactive: false,
      className: 'map-image-overlay',
    });
    overlayRef.current = overlay;
    // addTo registers Leaflet's zoom→_reset AFTER our zoom→onZoom above.
    overlay.addTo(map);
    // Leaflet creates the <img> synchronously when the layer is added,
    // so the element is available right after addTo.
    applyCrispRendering(overlay);
    promoteToCompositorLayer(overlay);

    // 2. Swap straight to the full image. The browser keeps painting the LQIP
    //    until the new src decodes, so first paint stays instant; once AVIF
    //    (preloaded, usually cache-hit) decodes it becomes the LCP element.
    //    Walk the format candidates via the overlay's error event instead of
    //    a separate preload probe.
    let candidateIdx = 0;
    function handleFullLoad() {
      if (cancelledRef.current) return;
      // Full image decoded — drop the crisp hint so the artwork interpolates
      // smoothly, and stop listening.
      clearCrispRendering(overlay);
      detachFullListeners();
    }
    function handleFullError() {
      if (cancelledRef.current) return;
      candidateIdx += 1;
      if (candidateIdx < FULL_IMAGE_CANDIDATES.length) {
        overlay.setUrl(FULL_IMAGE_CANDIDATES[candidateIdx]!);
      } else {
        // All formats failed — stay on the LQIP rather than show nothing.
        detachFullListeners();
        console.error('MapImageOverlay: failed to load any full image');
      }
    }
    function detachFullListeners() {
      overlay.off('load', handleFullLoad);
      overlay.off('error', handleFullError);
    }
    overlay.on('load', handleFullLoad);
    overlay.on('error', handleFullError);
    overlay.setUrl(FULL_IMAGE_CANDIDATES[0]!);

    return () => {
      cancelledRef.current = true;
      detachFullListeners();
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      map.off('zoom', onZoom);
      map.off('zoomend', onZoomEnd);
      const current = overlayRef.current;
      if (current) {
        map.removeLayer(current);
        overlayRef.current = null;
      }
    };
  }, [map]);

  return null;
}
